import React, { useState } from 'react';
import { motion, useScroll, useMotionValueEvent } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';

/* ── Navigation sets ── */
const RESIDENTIAL_NAV = [
  { label: 'Products', to: '/products' },
  { label: 'Downloads', to: '/downloads' },
  { label: 'About', to: '/about' },
];

const INDUSTRIAL_NAV = [
  { label: 'Solutions', to: '/solutions' },
  { label: 'Downloads', to: '/downloads' },
  { label: 'About', to: '/about' },
];

/* ── Nav link with underline hover ── */
function NavLink({ to, label }) {
  return (
    <Link
      to={to}
      className="relative px-4 py-1.5 text-[10px] font-semibold uppercase tracking-widest group transition-colors duration-300"
      style={{ color: 'var(--text-secondary)' }}
    >
      {label}
      <span
        className="absolute bottom-0 left-4 right-4 h-px origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-300"
        style={{ background: 'var(--iotiq-green)' }}
      />
    </Link>
  );
}

/**
 * HomeAutomationHeader — shown on all Home Automation and related pages:
 *   /home-automation, /home-automation/*, /products, /downloads, /about,
 *   /contact, /features, /faqs, /solutions, /services, /blog, /industrial-automation, etc.
 *
 * Layout: Logo (left) | Pill Nav (center) | Contact Us (right)
 * Behaviour: hides on scroll-down, reveals on scroll-up (smart hide).
 */
export default function HomeAutomationHeader() {
  const location = useLocation();
  const { scrollY } = useScroll();
  const [hidden, setHidden] = useState(false);
  const [prevY, setPrevY] = useState(0);

  /* Smart hide on scroll-down */
  useMotionValueEvent(scrollY, 'change', (latest) => {
    if (latest > prevY && latest > 80) setHidden(true);
    else setHidden(false);
    setPrevY(latest);
  });

  /* Determine nav links by section */
  const isIndustrial = location.pathname.startsWith('/industrial-automation');
  const navLinks = isIndustrial ? INDUSTRIAL_NAV : RESIDENTIAL_NAV;

  return (
    <header className="fixed top-0 left-0 right-0 z-[10000] pointer-events-none">
      <motion.div
        animate={{ y: hidden ? -80 : 0 }}
        transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
        className="relative w-full h-16 flex items-center justify-between px-6 md:px-12 pointer-events-auto"
        style={{
          // background: 'rgba(239,234,225,0.85)',
          backdropFilter: 'blur(32px) saturate(180%)',
          WebkitBackdropFilter: 'blur(32px) saturate(180%)',
          // borderBottom: '1px solid rgba(45,42,38,0.1)',
          // boxShadow: '0 4px 32px rgba(0,0,0,0.05)',
        }}
      >
        {/* LEFT: Logo + Section Label */}
        <div className="flex items-center gap-3 w-1/3">
          {/* Logo — links back to home */}
          <Link to="/" className="flex items-center group shrink-0">
            <motion.img
              src="/iotiq-logo.png"
              alt="IOTIQ"
              className="h-8 object-contain"
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.3 }}
            />
          </Link>

          {/* Divider + Section label */}
          <span
            className="hidden lg:block w-px h-5 self-center"
            style={{ background: 'rgba(45,42,38,0.15)' }}
          />
          <Link
            to={isIndustrial ? '/industrial-automation' : '/home-automation'}
            className="hidden lg:block text-[9px] font-mono uppercase tracking-[0.4em] transition-opacity duration-300 hover:opacity-70"
            style={{ color: 'var(--text-muted)' }}
          >
            {isIndustrial ? 'Industrial Automation' : 'Home Automation'}
          </Link>
        </div>

        {/* CENTER: Pill Nav */}
        <div className="flex justify-center w-1/3">
          {navLinks.length > 0 && (
            <nav
              className="hidden md:flex items-center gap-6 px-8 py-2.5 rounded-full"
              style={{
                background: 'rgba(239,234,225,0.85)',
                backdropFilter: 'blur(32px) saturate(180%)',
                WebkitBackdropFilter: 'blur(32px) saturate(180%)',
                border: '1px solid rgba(45,42,38,0.1)',
                boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
              }}
            >
              {navLinks.map((link) => (
                <NavLink key={link.label} {...link} />
              ))}
            </nav>
          )}
        </div>

        {/* RIGHT: Contact Us + Mobile Hamburger */}
        <div className="flex items-center justify-end w-1/3 gap-4">
          <Link
            to="/contact"
            className="hidden md:inline-flex items-center px-6 py-2.5 rounded-full text-[10px] font-bold uppercase tracking-ultra transition-all duration-400"
            style={{
              color: 'var(--iotiq-green)',
              border: '1px solid var(--iotiq-green)',
              background: 'transparent',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--iotiq-green)';
              e.currentTarget.style.color = 'white';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = 'var(--iotiq-green)';
            }}
          >
            Contact Us
          </Link>

          {/* Mobile hamburger */}
          <div className="md:hidden">
            <button
              aria-label="Open menu"
              className="w-10 h-10 rounded-full flex items-center justify-center border transition-colors hover:bg-black/5"
              style={{ color: 'var(--iotiq-green)', borderColor: 'rgba(45,42,38,0.12)' }}
            >
              <i className="fas fa-bars text-sm" />
            </button>
          </div>
        </div>
      </motion.div>
    </header>
  );
}
